import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";

import { Box, Text, HStack, IconButton, VStack } from "@chakra-ui/react";
import colors from "app/colors";
import {
  DownloadIcon,
  PauseIcon,
  PlayIcon,
  TrashOutlineIcon,
} from "modules/icons";
import { FileGalleryFragment, MessageThreadFragment } from "gql/graphql";
import moment from "moment";
import { useWavesurfer } from "@wavesurfer/react";
import UserName from "../../../_shared/user-name";
import { MESSAGE_CALENDAR_FORMATS } from "modules/utils/Constants";
import CircularIconButton from "../../../_shared/buttons/circular-icon-button";
import MessageLink from "./message-link";
import MessageFileDrawerReactionsPopover from "./message-file-drawer-reactions-popover";
import { useAuth } from "modules/providers/auth-provider";
import useDownloadFile from "modules/hooks/use-download-file";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import MessageFileReactions from "modules/messaging/messages/message-file-reactions";

interface IProps {
  messageThread?: MessageThreadFragment | null;
  file: FileGalleryFragment;
}

const MessageThreadFilesDrawerAudioFile = ({ messageThread, file }: IProps) => {
  const { me } = useAuth();
  const { downloadFile } = useDownloadFile(file);
  const { isMeRemoved } = useRemovedFromThread(messageThread);
  const { openModal } = useOverlay();
  const containerRef = useRef<HTMLDivElement>(null);

  const onDeleteFileClicked = useCallback(
    (fileId: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!messageThread) return;
      openModal({
        type: MODAL_TYPE.DELETE_FILE,
        id: fileId,
        id2: messageThread.id,
      });
    },
    [messageThread, openModal],
  );

  const { wavesurfer, isPlaying, currentTime, isReady } = useWavesurfer({
    container: containerRef,
    height: 15,
    barWidth: 2,
    barGap: 3,
    cursorColor: colors.gray50,
    cursorWidth: 2,
    waveColor: colors.gray30,
    progressColor: colors.black,
    //https://github.com/katspaugh/wavesurfer.js/discussions/3831
    //hopefully this will be fixed in the future
    // url: file.cloudFrontUrl || undefined,
    // fetchParams: {
    //   credentials: "same-origin",
    // },
  });

  useEffect(() => {
    if (!file.cloudFrontUrl || !wavesurfer || isReady) return;
    fetch(file.cloudFrontUrl, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.blob())
      .then((blob) => wavesurfer?.loadBlob(blob));
  }, [file.cloudFrontUrl, isReady, wavesurfer]);

  const seconds = useMemo(() => {
    if (!isReady) return "00:00";
    const duration = wavesurfer ? wavesurfer.getDuration() : 0;
    const timeDisplay = currentTime > 0 ? currentTime : duration;
    return moment.utc(timeDisplay * 1000).format("mm:ss");
  }, [currentTime, wavesurfer, isReady]);

  const onPlayClicked = useCallback(() => {
    wavesurfer?.playPause();
  }, [wavesurfer]);

  if (!me) return null;

  return (
    <Box
      h={118}
      alignContent="center"
      w="full"
      borderBottomWidth={1}
      borderBottomColor={colors.gray0}
    >
      <HStack spacing={4} h={86} w="full">
        <IconButton
          minW={86}
          minH={86}
          borderColor={colors.grayPrimary}
          borderWidth={1}
          borderRadius="6px"
          icon={
            isPlaying ? (
              <PauseIcon fill={colors.blue} />
            ) : (
              <PlayIcon fill={colors.blue} />
            )
          }
          aria-label="play"
          variant="ghost"
          onClick={onPlayClicked}
        />
        <VStack alignItems="flex-start" spacing={0} w="full">
          <Text
            fontSize={16}
            fontWeight={700}
            noOfLines={1}
            color={colors.blackSecondary}
            wordBreak="break-all"
          >
            {file.name}
          </Text>
          <HStack
            color={colors.gray50}
            fontSize={14}
            fontWeight={400}
            spacing={1}
          >
            <Text>
              <UserName userId={file.userId} />
            </Text>
            <Text>•</Text>
            <Text>
              {moment(file.createTime).calendar(MESSAGE_CALENDAR_FORMATS)}
            </Text>
          </HStack>
          <MessageLink
            messageThreadId={file.messageThreadId}
            messageId={file.messageId}
            isVoiceNote={file.isVoiceNote}
          />
          {file.reactionList.length > 0 && (
            <MessageFileDrawerReactionsPopover file={file}>
              <MessageFileReactions
                reactions={file.reactionList}
                color={colors.gray50}
              />
            </MessageFileDrawerReactionsPopover>
          )}
          <HStack w="full" spacing={0}>
            <Box ref={containerRef} w="full" />
            <Text color={colors.grayMain} fontSize={12}>
              {seconds}
            </Text>
          </HStack>
        </VStack>
        <HStack spacing={4} h="full" alignItems="flex-start">
          {file.userId === me.id && !isMeRemoved && (
            <CircularIconButton
              minW={8}
              h={8}
              aria-label="delete"
              color={colors.gray40}
              backgroundColor={colors.gray40}
              isDisabled={me.id !== file.userId}
              icon={<TrashOutlineIcon boxSize={4} fill={colors.white} />}
              onClick={onDeleteFileClicked(file.id)}
            />
          )}
          <CircularIconButton
            minW={8}
            h={8}
            aria-label="download"
            color={colors.gray40}
            backgroundColor={colors.gray40}
            onClick={downloadFile}
            icon={<DownloadIcon boxSize={4} fill={colors.white} />}
          />
        </HStack>
      </HStack>
    </Box>
  );
};

export default memo(MessageThreadFilesDrawerAudioFile);
